import { fmtMoney } from '../utils/currency';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  TrendingUp, DollarSign, ShoppingCart, ClipboardList,
  CalendarDays, AlertTriangle, BarChart3, Flame, Banknote,
  RefreshCw,
} from 'lucide-react';
import {
  useToast, PageHeader, Button, Input, EmptyState, FilterTabs, SkeletonCard,
} from '../components/ui';

const API = process.env.REACT_APP_API_URL || '/api';

const TINT = {
  primary: 'bg-primary-50 text-primary-600',
  success: 'bg-success-50 text-success-600',
  warning: 'bg-warning-50 text-warning-600',
  danger:  'bg-danger-50 text-danger-600',
};

const SectionCard = ({ title, icon: Icon, right, children, className = '' }) => (
  <div className={`bg-white rounded-lg border border-neutral-200 shadow-card overflow-hidden ${className}`}>
    <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
      <h2 className="text-sm font-semibold text-neutral-800 flex items-center gap-2">
        {Icon && <Icon size={14} className="text-neutral-500" />}
        {title}
      </h2>
      {right}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const formatHour = (hour) => {
  if (hour === 0) return '12 AM';
  if (hour < 12) return `${hour} AM`;
  if (hour === 12) return '12 PM';
  return `${hour - 12} PM`;
};

const Analytics = () => {
  const { toast } = useToast();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [period, setPeriod] = useState('daily');
  const [dashboard, setDashboard] = useState(null);
  const [salesByPeriod, setSalesByPeriod] = useState([]);
  const [orderTypes, setOrderTypes] = useState([]);
  const [popularItems, setPopularItems] = useState([]);
  const [peakHours, setPeakHours] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
    setStartDate(firstDay);
    setEndDate(lastDay);
  }, []);

  useEffect(() => {
    if (startDate && endDate) fetchAllData();
  }, [startDate, endDate, period]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'x-auth-token': token };

      const [
        dashboardRes,
        salesRes,
        orderTypesRes,
        popularRes,
        peakHoursRes,
        paymentRes
      ] = await Promise.all([
        axios.get(`${API}/analytics/dashboard`, { headers }),
        axios.get(`${API}/analytics/sales-by-period?startDate=${startDate}&endDate=${endDate}&period=${period}`, { headers }),
        axios.get(`${API}/analytics/order-types?startDate=${startDate}&endDate=${endDate}`, { headers }),
        axios.get(`${API}/analytics/popular-items?limit=10`, { headers }),
        axios.get(`${API}/analytics/peak-hours?startDate=${startDate}&endDate=${endDate}`, { headers }),
        axios.get(`${API}/analytics/payment-methods?startDate=${startDate}&endDate=${endDate}`, { headers })
      ]);

      setDashboard(dashboardRes.data);
      setSalesByPeriod(salesRes.data);
      setOrderTypes(orderTypesRes.data);
      setPopularItems(popularRes.data);
      setPeakHours(peakHoursRes.data);
      setPaymentMethods(paymentRes.data);
    } catch (err) {
      console.error('Analytics error:', err);
      toast.error(err.response?.data?.msg || 'Could not load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const dashStats = [
    { key: 'sales',   label: "Today's sales",   color: 'success', Icon: DollarSign,
      fmt: () => fmtMoney(dashboard?.todaySales || 0, true) },
    { key: 'orders',  label: "Today's orders",  color: 'primary', Icon: ShoppingCart,
      fmt: () => dashboard?.todayOrders || 0 },
    { key: 'active',  label: 'Active orders',   color: 'warning', Icon: ClipboardList,
      fmt: () => dashboard?.activeOrders || 0 },
    { key: 'res',     label: 'Reservations',    color: 'primary', Icon: CalendarDays,
      fmt: () => dashboard?.todayReservations || 0 },
    { key: 'low',     label: 'Low stock',       color: 'danger',  Icon: AlertTriangle,
      fmt: () => dashboard?.lowStockItems || 0 },
  ];

  const maxPeak = peakHours.length
    ? Math.max(...peakHours.map(h => h.orderCount))
    : 0;

  return (
    <>
      <PageHeader
        title="Analytics & Reports"
        subtitle="Sales trends, order breakdowns, peak hours, and payment mix."
      />

      {/* Date filters */}
      <div className="bg-white rounded-lg border border-neutral-200 shadow-card p-4 mb-6 flex flex-wrap items-end gap-3">
        <div className="w-44">
          <Input
            label="Start date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="w-44">
          <Input
            label="End date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div className="flex-1 min-w-[180px]">
          <p className="text-sm font-medium text-neutral-700 mb-1">Group by</p>
          <FilterTabs
            value={period}
            onChange={setPeriod}
            options={['daily', 'weekly', 'monthly']}
          />
        </div>
        <Button variant="secondary" leftIcon={<RefreshCw size={14} />} onClick={fetchAllData} loading={loading}>
          Refresh
        </Button>
      </div>

      {/* Dashboard summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        {dashStats.map(({ key, label, color, Icon, fmt }) => (
          <div key={key} className="bg-white rounded-lg border border-neutral-200 shadow-card p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{label}</p>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${TINT[color]}`}>
                <Icon size={18} />
              </div>
            </div>
            <p className="text-2xl font-bold text-neutral-900 tabular-nums">
              {loading && !dashboard ? '…' : fmt()}
            </p>
          </div>
        ))}
      </div>

      {/* Loading skeletons for the chart row */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Sales by period */}
          <SectionCard
            title={`Sales trend (${period})`}
            icon={TrendingUp}
            right={<span className="text-xs text-neutral-500">{salesByPeriod.length} period{salesByPeriod.length === 1 ? '' : 's'}</span>}
          >
            {salesByPeriod.length === 0 ? (
              <EmptyState icon={TrendingUp} title="No sales data" message="Try widening the date range." compact />
            ) : (
              <ul className="space-y-1 max-h-64 overflow-y-auto pr-1">
                {salesByPeriod.map((item, idx) => (
                  <li key={idx} className="flex justify-between items-center py-2 border-b border-neutral-100 last:border-b-0">
                    <span className="text-sm font-medium text-neutral-800">{item._id}</span>
                    <div className="text-right">
                      <p className="text-sm font-semibold tabular-nums">{fmtMoney(item.sales || 0)}</p>
                      <p className="text-[11px] text-neutral-500">{item.orders} order{item.orders === 1 ? '' : 's'}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>

          {/* Order type breakdown */}
          <SectionCard
            title="Order types"
            icon={BarChart3}
            right={<span className="text-xs text-neutral-500">{orderTypes.length} type{orderTypes.length === 1 ? '' : 's'}</span>}
          >
            {orderTypes.length === 0 ? (
              <EmptyState icon={BarChart3} title="No orders in this range" compact />
            ) : (
              <ul className="space-y-2">
                {orderTypes.map((type, idx) => {
                  const maxRev = Math.max(...orderTypes.map(t => t.revenue || 0));
                  const pct = maxRev > 0 ? ((type.revenue || 0) / maxRev) * 100 : 0;
                  return (
                    <li key={idx}>
                      <div className="flex justify-between items-baseline mb-1">
                        <span className="text-sm font-medium capitalize text-neutral-800">{type._id || 'unknown'}</span>
                        <div className="text-right">
                          <span className="text-sm font-semibold tabular-nums">{fmtMoney(type.revenue || 0)}</span>
                          <span className="text-[11px] text-neutral-500 ml-2">{type.count} order{type.count === 1 ? '' : 's'}</span>
                        </div>
                      </div>
                      <div className="w-full bg-neutral-100 rounded-full h-1.5 overflow-hidden">
                        <div className="bg-primary-500 h-full rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </SectionCard>

          {/* Popular items */}
          <SectionCard
            title="Popular items"
            icon={Flame}
            right={<span className="text-xs text-neutral-500">Top {popularItems.length}</span>}
          >
            {popularItems.length === 0 ? (
              <EmptyState icon={Flame} title="No item data yet" compact />
            ) : (
              <ul className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {popularItems.map((item, idx) => (
                  <li key={idx} className="flex items-center justify-between gap-3 py-1.5 border-b border-neutral-100 last:border-b-0">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-6 h-6 rounded bg-neutral-100 text-neutral-600 text-[11px] font-bold flex items-center justify-center flex-shrink-0">
                        {idx + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-neutral-800 truncate">{item.name || item._id}</p>
                        <p className="text-[11px] text-neutral-500 tabular-nums">{item.quantitySold} sold</p>
                      </div>
                    </div>
                    <p className="text-sm font-semibold tabular-nums whitespace-nowrap">
                      {fmtMoney(item.revenue || 0, true)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>

          {/* Peak hours */}
          <SectionCard
            title="Peak hours"
            icon={TrendingUp}
            right={<span className="text-xs text-neutral-500">Top 10</span>}
          >
            {peakHours.length === 0 ? (
              <EmptyState icon={TrendingUp} title="No hourly data" compact />
            ) : (
              <ul className="space-y-2">
                {[...peakHours]
                  .sort((a, b) => b.orderCount - a.orderCount)
                  .slice(0, 10)
                  .map((hour, idx) => {
                    const pct = maxPeak > 0 ? (hour.orderCount / maxPeak) * 100 : 0;
                    return (
                      <li key={idx} className="flex items-center gap-3">
                        <span className="text-sm font-medium w-14 text-neutral-700 tabular-nums">{formatHour(hour._id)}</span>
                        <div className="flex-1 bg-neutral-100 rounded-full h-2 overflow-hidden">
                          <div className="bg-primary-500 h-full rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-[11px] text-neutral-500 w-16 text-right tabular-nums">
                          {hour.orderCount} order{hour.orderCount === 1 ? '' : 's'}
                        </span>
                      </li>
                    );
                  })}
              </ul>
            )}
          </SectionCard>
        </div>
      )}

      {/* Payment methods */}
      <SectionCard
        title="Payment methods"
        icon={Banknote}
        right={<span className="text-xs text-neutral-500">{paymentMethods.length} method{paymentMethods.length === 1 ? '' : 's'}</span>}
      >
        {paymentMethods.length === 0 ? (
          <EmptyState icon={Banknote} title="No transactions in this range" compact />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {paymentMethods.map((method, idx) => (
              <div key={idx} className="border border-neutral-200 rounded-lg p-4 bg-neutral-50/50">
                <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1">
                  {(method._id || 'unknown').replace('_', ' ')}
                </p>
                <p className="text-2xl font-bold text-neutral-900 tabular-nums">
                  {fmtMoney(method.total || 0, true)}
                </p>
                <p className="text-xs text-neutral-500 mt-1 tabular-nums">
                  {method.count} transaction{method.count === 1 ? '' : 's'}
                </p>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </>
  );
};

export default Analytics;
